import { Commit } from 'vuex'

import CreditCard from '@/models/credit-card'
import NewCreditCardForm from '@/models/new-credit-card-form'

import axios from '@/axios-module'
import notification from '@/notification'

export enum CreditCardActionTypes {
  Query = '[CreditCard] Query',
  DeleteCard = '[CreditCard] DeleteCard',
  AddCard = '[CreditCard] AddCard',
}

export enum CreditCardMutationTypes {
  QuerySuccess = '[CreditCard] QuerySuccess',
  DeleteCard = '[CreditCard] DeleteCard',
  DeleteCardFailure = '[CreditCard] DeleteCardFailure',
  AddCard = '[CreditCard] AddCard',
  AddCardSuccess = '[CreditCard] AddCardSuccess',
  AddCardFailure = '[CreditCard] AddCardFailure',
  Reset = '[CreditCard] Reset',
}

export interface State {
  entities: {
    [id: number]: CreditCard
  }
  ids: number[]
}

function initialState(): State {
  return {
    entities: {},
    ids: [],
  }
}

export default {
  state: initialState,
  getters: {
    getCreditCardById(state: State): (id: number) => CreditCard | null {
      return (id: number) => state.entities[id] || null
    },
    getCreditCards(state: State): CreditCard[] {
      return state.ids.map((id) => state.entities[id])
    },
  },
  actions: {
    async [CreditCardActionTypes.Query](
      { commit }: { commit: Commit },
      bookingId: number,
    ) {
      const params = bookingId ? { booking_id: bookingId } : {}
      const response = await axios.get('/customer/credit_cards', { params })
      commit(CreditCardMutationTypes.QuerySuccess, response.data)
    },
    async [CreditCardActionTypes.DeleteCard](
      { commit, state }: { commit: Commit; state: State },
      id: number,
    ) {
      const oldIds = state.ids
      commit(CreditCardMutationTypes.DeleteCard, id)
      try {
        await axios.delete(`/customer/credit_cards/${id}`)
        notification('Payment method was removed!', 'success')
      } catch ({ response }) {
        const message =
          response.data.stripe || 'Payment method was not updated!'
        notification(message, 'error')
        commit(CreditCardMutationTypes.DeleteCardFailure, oldIds)
      }
    },
    async [CreditCardActionTypes.AddCard](
      { commit }: { commit: Commit },
      cardForm: NewCreditCardForm,
    ) {
      commit(CreditCardMutationTypes.AddCard)
      try {
        const response = await axios.post('/customer/credit_cards', {
          creditCard: cardForm,
        })
        notification('New payment method added!', 'success')
        commit(CreditCardMutationTypes.AddCardSuccess, response.data)
      } catch ({ response }) {
        const message = 'Failure adding new payment method...'
        notification(message, 'error')
        commit(CreditCardMutationTypes.AddCardFailure, response.data)
      }
    },
  },
  mutations: {
    [CreditCardMutationTypes.QuerySuccess](
      state: State,
      creditCards: CreditCard[],
    ) {
      const { entities, ids } = creditCards.reduce(
        (
          carry: { entities: { [id: string]: CreditCard }; ids: number[] },
          card: CreditCard,
        ) => {
          carry.entities[card.id] = card
          carry.ids.push(card.id)
          return carry
        },
        { entities: {}, ids: [] },
      )

      state.entities = entities
      state.ids = ids
    },
    [CreditCardMutationTypes.DeleteCard](state: State, id: number) {
      state.ids = state.ids.filter((sId) => sId !== id)
    },
    [CreditCardMutationTypes.DeleteCardFailure](
      state: State,
      oldIds: number[],
    ) {
      state.ids = oldIds
    },
    [CreditCardMutationTypes.AddCardSuccess](state: State, card: CreditCard) {
      state.entities[card.id] = card
      state.ids.push(card.id)
    },
    [CreditCardMutationTypes.Reset](state: State) {
      Object.assign(state, initialState())
    },
  },
}
